import { useContext } from 'react'

import { Button, ModalContext, SidebarModal } from '@domain-group/suit-theme'

import useCurrentUser from '@hooks/useCurrentUser'

import { getCampaignPermissions } from '@functions/campaign'

import { digObject } from '@domain-group/javascript-utils'
import AgencyMarketingButton from './components/AgencyMarketing'
import ImportCampaignButton from './components/ImportCampaign'
import PropertyMarketing from './components/PropertyMarketing'

const MODAL_KEY = 'NewCampaignModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const NewCampaignModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const { agency, currentUser, permissions } = useCurrentUser()

  const propertyMarketingPermissions = getCampaignPermissions({
    user: currentUser,
    campaignType: 'property_marketing',
  })

  const agencyMarketingPermissions = getCampaignPermissions({
    user: currentUser,
    campaignType: 'agency_marketing',
  })

  const canCreateAgencyMarketingCampaigns = agencyMarketingPermissions?.details?.create || false
  const canCreatePropertyMarketingCampaigns = propertyMarketingPermissions?.details?.create || false
  const canImportCampaigns = (canCreateAgencyMarketingCampaigns || canCreatePropertyMarketingCampaigns)
  && permissions?.agency?.integrations?.crm_sync?.import
  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const { shouldLaunchModal } = modalPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Start" titleSecondLine="New..." />

      <SidebarModal.Content>
        {canCreateAgencyMarketingCampaigns && (
          <AgencyMarketingButton shouldLaunchModal={shouldLaunchModal} />
        )}

        {canCreatePropertyMarketingCampaigns && (
          <PropertyMarketing />
        )}

        {canImportCampaigns && <ImportCampaignButton agencyId={agency.id} />}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close Modal
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <NewCampaignModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
